"use client";

import { Link } from "@/i18n/routing";
import { CarouselButton, DotButton } from "@repo/components";
import { useCarouselButtons, useDotButton } from "@repo/lib/hooks/common";
import { getStrapiMedia, shimmer, toBase64 } from "@repo/lib/utils";
import { Button } from "@repo/ui";
import cx from "classnames";
import useEmblaCarousel from "embla-carousel-react";
import { useLocale } from "next-intl";
import Image from "next/image";
const Hero = ({
  data
}) => {
  const {
    BannerSlide,
    contentPosition,
    textColor
  } = data;
  const locale = useLocale();
  const isRTL = locale === "ar";
  const OPTIONS = {
    slidesToScroll: 1,
    loop: true,
    direction: isRTL ? "rtl" : "ltr",
    align: "start"
  };
  const [emblaRef, emblaApi] = useEmblaCarousel(OPTIONS);
  const {
    selectedIndex,
    scrollSnaps,
    onDotButtonClick
  } = useDotButton(emblaApi);
  const {
    prevBtnDisabled,
    nextBtnDisabled,
    onPrevButtonClick,
    onNextButtonClick
  } = useCarouselButtons(emblaApi);
  const isCarouselRequired = BannerSlide.length > 1;
  if (BannerSlide.length === 0) return null;
  const BannerImage = ({
    slide,
    isMobile
  }) => {
    const media = isMobile ? slide?.mobileMedia?.file : slide?.desktopMedia?.file;
    if (!media?.url) return null;
    return <Image src={getStrapiMedia(media.url)} alt={media.alternativeText || `Banner image ${slide?.id}`} width={media.width} height={media.height} className={`mx-auto h-full w-full object-cover md:max-h-[600px] 2xl:max-h-[720px] ${isMobile ? "h-auto min-h-56 md:hidden" : "hidden md:block"}`} placeholder={`data:image/svg+xml;base64,${toBase64(shimmer(media.width, media.height))}`} priority={slide.id === BannerSlide[0].id} fetchPriority={slide.id === BannerSlide[0].id ? "high" : "auto"} decoding="async" loading={slide.id === BannerSlide[0].id ? "eager" : "lazy"} data-sentry-element="Image" data-sentry-component="BannerImage" data-sentry-source-file="Hero.jsx" />;
  };
  const BannerOverlay = ({
    slide
  }) => {
    const hasNoContent = !slide?.title && !slide?.subtitle && !slide?.link?.text;
    const {
      text,
      icon,
      showIcon,
      isTransparent
    } = slide?.link || {};
    if (hasNoContent) return null;
    const position = contentPosition || "bottom";
    return <div className={cx("group absolute inset-0 z-[2] flex", {
      "items-end justify-center": position === "bottom",
      "items-start justify-center": position === "top",
      "items-center justify-center": position === "center",
      "items-center justify-start rtl:justify-end": position === "left",
      "items-center justify-end rtl:justify-start": position === "right"
    })} data-sentry-component="BannerOverlay" data-sentry-source-file="Hero.jsx">
        <div className={cx("mx-auto flex w-full px-4 md:max-w-screen-lg md:px-8 lg:px-16 2xl:max-w-screen-xl", {
        "justify-center pb-8 md:pb-4 lg:pb-10": position === "bottom",
        "justify-center pt-6 md:pt-10": position === "top",
        "justify-start md:ms-6 lg:mx-auto rtl:justify-end": position === "left",
        "justify-end md:ms-6 lg:mx-auto rtl:justify-start": position === "right",
        "justify-center": position === "center"
      })}>
          <div className={cx("flex flex-col gap-2 p-3 md:gap-4 md:p-6 lg:gap-6 2xl:gap-8", {
          "w-full max-w-[900px] items-center text-center": position === "bottom" || position === "top" || position === "center",
          "max-w-[260px] md:max-w-[700px] md:justify-center": position === "left" || position === "right"
        })}>
            {slide?.title && <h1 className={cx("font-heading rtl:font-arabic text-2xl font-normal md:text-5xl lg:text-7xl lg:leading-[5rem]", {
            "text-white": textColor === "light",
            "text-black": textColor === "dark",
            "text-center": position === "bottom" || position === "top" || position === "center",
            "text-start": position === "left",
            "text-end rtl:text-start": position === "right"
          })}>
                {slide.title}
              </h1>}
            {slide?.subtitle && <p className={cx("font-medium md:max-w-xl lg:[max-width:inherit]", {
            "text-white": textColor === "light",
            "text-black": textColor == "dark",
            "text-center text-xs md:text-xl": position === "bottom" || position === "top" || position === "center",
            "text-start text-lg md:text-4xl lg:text-[48px]": position === "left",
            "text-end text-lg md:text-4xl lg:text-[48px] rtl:text-start": position === "right"
          })}>
                {slide.subtitle}
              </p>}
            {slide?.link?.url && slide?.link?.text && <Button variant={isTransparent ? "ghost" : "primary"} size="md" className={cx("rounded-banner-button border-button-primary-border flex max-w-min items-center gap-2", {
            "border-none !bg-transparent": isTransparent,
            "text-white": textColor === "light",
            "text-black": textColor === "dark",
            "mx-auto": position === "bottom" || position === "top" || position === "center",
            "me-auto": position === "left",
            "ms-auto rtl:ms-0": position === "right"
          })}>
                <span className="whitespace-nowrap text-sm md:text-lg">
                  {slide.link.text}
                </span>
                {showIcon && icon?.url && <Image src={getStrapiMedia(icon?.url)} alt={icon?.alternativeText || text} width={icon?.width} height={icon?.height} className="me-2 size-[14px] transition-all duration-300 group-hover:translate-x-2 md:me-0 rtl:rotate-180 rtl:group-hover:-translate-x-2" />}
              </Button>}
          </div>
        </div>
      </div>;
  };
  return <section className="relative m-auto mb-5 w-full md:mb-8" data-sentry-component="Hero" data-sentry-source-file="Hero.jsx">
      {/* Carousel Buttons */}
      {isCarouselRequired && <div className="pointer-events-none absolute inset-0 z-30 hidden items-center justify-between md:flex">
          <div className="mx-auto flex w-full items-center justify-between px-4 md:max-w-screen-xl md:px-8 lg:px-16 2xl:max-w-screen-2xl">
            <CarouselButton direction="prev" onClick={onPrevButtonClick} disabled={prevBtnDisabled} className="text-hero-button-icon bg-hero-button-bg hover:bg-hero-button-bg disabled:bg-hero-button-disabledBg disabled:text-button-disabled-text pointer-events-auto rounded-full fill-current p-2 shadow-md transition-colors" />
            <CarouselButton direction="next" onClick={onNextButtonClick} disabled={nextBtnDisabled} className="hover:bg-hero-button-bg text-hero-button-icon bg-hero-button-bg disabled:bg-hero-button-disabledBg disabled:text-button-disabled-text pointer-events-auto rounded-full p-2 shadow-md transition-colors" />
          </div>
        </div>}

      <div className="mx-auto w-full">
        <div className="overflow-hidden" ref={isCarouselRequired ? emblaRef : null}>
          <div className="flex">
            {BannerSlide?.map(slide => <Link href={slide?.link?.url || "/"} aria-label={slide?.title || slide?.desktopMedia?.file?.alternativeText} key={slide.id} className="relative w-full flex-none">
                <BannerImage slide={slide} isMobile={true} />
                <BannerImage slide={slide} isMobile={false} />
                <div className={cx("absolute inset-0 z-[1]", {
              "bg-[linear-gradient(180deg,rgba(0,0,0,0)_40%,rgba(0,0,0,0.7)_100%)]": contentPosition === "bottom",
              "bg-[linear-gradient(0deg,rgba(0,0,0,0)_40%,rgba(0,0,0,0.7)_100%)]": contentPosition === "top",
              "bg-[linear-gradient(90deg,#FFFFFF4D_30%,#99999900_100%)]": contentPosition === "left",
              "bg-[linear-gradient(270deg,#FFFFFF4D_30%,#99999900_100%)]": contentPosition === "right"
            })}></div>
                <BannerOverlay slide={slide} />
              </Link>)}
          </div>
        </div>
      </div>

      {/* Carousel Dots */}
      {isCarouselRequired && <div className="absolute bottom-6 left-1/2 z-30 flex -translate-x-1/2 items-center justify-center space-x-2 rtl:space-x-reverse">
          {scrollSnaps.map((_, index) => <DotButton key={index} onClick={() => onDotButtonClick(index)} className={cx("pointer-events-auto h-2 w-2 rounded-full", {
        "bg-hero-dot-active": index === selectedIndex,
        "bg-hero-dot-inactive": index !== selectedIndex
      })} aria-label={index} />)}
        </div>}
    </section>;
};
export default Hero;